import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Alert,
  Box,
  LinearProgress,
  Chip,
  FormControlLabel,
  Switch,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface ExtractedPrompt {
  name: string;
  content: string;
  category?: string;
  tags?: string[];
}

interface ImportResult {
  packId: string;
  packName: string;
  promptsImported: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: (result: ImportResult) => void;
  extractFn: (payload: { text: string }) => Promise<{
    data: {
      packName?: string;
      description?: string;
      suggestedTags?: string[];
      prompts: ExtractedPrompt[];
    };
  }>;
  saveFn: (payload: {
    prompts: ExtractedPrompt[];
    packName: string;
    description: string;
    tags: string[];
    isPublic: boolean;
  }) => Promise<{ data: { entityId: string; promptPackName: string }; meta: { promptsImported: number } }>;
}

type Step = 'input' | 'review';

export default function ImportPromptPackDialog({ open, onClose, onImported, extractFn, saveFn }: Props) {
  const [step, setStep] = useState<Step>('input');

  // Step 1 state
  const [text, setText] = useState('');

  // Step 2 state
  const [reviewPrompts, setReviewPrompts] = useState<ExtractedPrompt[]>([]);
  const [packName, setPackName] = useState('');
  const [description, setDescription] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (loading) return;
    // Reset all state
    setStep('input');
    setText('');
    setReviewPrompts([]);
    setPackName('');
    setDescription('');
    setTagsInput('');
    setIsPublic(false);
    setError(null);
    onClose();
  };

  const handleExtract = async () => {
    if (!text.trim()) {
      setError('Paste some text containing prompts to extract.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await extractFn({ text: text.trim() });
      setReviewPrompts(result.data.prompts);
      setPackName(result.data.packName ?? '');
      setDescription(result.data.description ?? '');
      setTagsInput((result.data.suggestedTags ?? []).join(', '));
      setStep('review');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Extraction failed';
      if (msg.includes('402') || msg.toLowerCase().includes('token balance')) {
        setError('Insufficient token balance — upgrade your plan to use AI-powered import.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePrompt = (index: number) => {
    setReviewPrompts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (reviewPrompts.length === 0) {
      setError('Add at least one prompt before importing.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const tags = tagsInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const result = await saveFn({
        prompts: reviewPrompts,
        packName: packName.trim() || 'Imported Prompt Pack',
        description: description.trim(),
        tags,
        isPublic,
      });

      onImported({
        packId: result.data.entityId,
        packName: result.data.promptPackName,
        promptsImported: result.meta.promptsImported,
      });
      handleClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Import failed';
      if (msg.includes('402') || msg.toLowerCase().includes('token balance')) {
        setError('Insufficient token balance — upgrade your plan to use AI-powered import.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const charCount = text.length;
  const charLimit = 100_000;
  const overLimit = charCount > charLimit;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UploadFileIcon fontSize="small" />
        {step === 'input' ? 'Import Prompt Pack' : `Review Extracted Prompts (${reviewPrompts.length})`}
      </DialogTitle>

      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {step === 'input' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Paste any text that contains prompts — numbered lists, markdown docs, research outputs, or
              raw notes. The AI will extract the individual prompts for you to review before saving.
            </Typography>

            <TextField
              label="Source text"
              multiline
              minRows={10}
              maxRows={18}
              fullWidth
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={loading}
              placeholder="Paste your prompt list, research doc, or notes here…"
              error={overLimit}
              helperText={
                overLimit
                  ? `${charCount.toLocaleString()} / ${charLimit.toLocaleString()} characters (over limit)`
                  : `${charCount.toLocaleString()} / ${charLimit.toLocaleString()} characters`
              }
            />
          </>
        )}

        {step === 'review' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review the extracted prompts below. Remove any you don't want, then edit the pack details before importing.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="Pack name"
                fullWidth
                value={packName}
                onChange={(e) => setPackName(e.target.value)}
                disabled={loading}
                inputProps={{ maxLength: 200 }}
              />
              <TextField
                label="Tags (comma-separated)"
                fullWidth
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                disabled={loading}
              />
            </Box>

            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={loading}
              inputProps={{ maxLength: 2000 }}
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Make pack public"
              sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Prompts — click × to remove
            </Typography>

            <List
              dense
              disablePadding
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 320,
                overflow: 'auto',
              }}
            >
              {reviewPrompts.map((prompt, index) => (
                <Box key={index}>
                  {index > 0 && <Divider />}
                  <ListItem
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemovePrompt(index)}
                        disabled={loading}
                        aria-label={`Remove "${prompt.name}"`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={600}>
                            {prompt.name}
                          </Typography>
                          {prompt.category && (
                            <Chip label={prompt.category} size="small" variant="outlined" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {prompt.content.length > 120
                            ? `${prompt.content.slice(0, 120)}…`
                            : prompt.content}
                        </Typography>
                      }
                    />
                  </ListItem>
                </Box>
              ))}
              {reviewPrompts.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        All prompts removed — go back and try different text.
                      </Typography>
                    }
                  />
                </ListItem>
              )}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {step === 'input' && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleExtract}
              disabled={loading || !text.trim() || overLimit}
              startIcon={<UploadFileIcon />}
            >
              {loading ? 'Extracting prompts…' : 'Extract Prompts'}
            </Button>
          </>
        )}

        {step === 'review' && (
          <>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => { setStep('input'); setError(null); }}
              disabled={loading}
            >
              Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || reviewPrompts.length === 0}
              startIcon={<UploadFileIcon />}
            >
              {loading ? 'Importing…' : `Import ${reviewPrompts.length} Prompt${reviewPrompts.length !== 1 ? 's' : ''}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
